import { useEffect, useRef, useState } from "react";

export default function Cursor() {
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState("default");
  const dotRef = useRef(null);
  const ringRef = useRef(null);

  useEffect(() => {
    const fine = window.matchMedia("(pointer: fine) and (hover: hover)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (fine && !reduced) {
      setEnabled(true);
      document.documentElement.classList.add("has-cursor");
    }
    return () => document.documentElement.classList.remove("has-cursor");
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let mx = -100, my = -100, rx = -100, ry = -100, raf;
    const move = (e) => {
      mx = e.clientX;
      my = e.clientY;
      if (dotRef.current) dotRef.current.style.transform = `translate3d(${mx}px, ${my}px, 0)`;
    };
    const loop = () => {
      rx += (mx - rx) * 0.16;
      ry += (my - ry) * 0.16;
      if (ringRef.current) ringRef.current.style.transform = `translate3d(${rx}px, ${ry}px, 0)`;
      raf = requestAnimationFrame(loop);
    };
    const over = (e) => {
      if (!e.target || !e.target.closest) return;
      if (e.target.closest("[data-cursor='view']")) setMode("view");
      else if (e.target.closest("a,button,[role='button'],label,input,textarea,select")) setMode("link");
      else setMode("default");
    };
    window.addEventListener("mousemove", move, { passive: true });
    document.addEventListener("mouseover", over, { passive: true });
    raf = requestAnimationFrame(loop);
    return () => {
      window.removeEventListener("mousemove", move);
      document.removeEventListener("mouseover", over);
      cancelAnimationFrame(raf);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-[9998]" aria-hidden="true">
      <div ref={dotRef} className="absolute left-0 top-0 will-change-transform">
        <span
          className={`-ml-1 -mt-1 block h-2 w-2 rounded-full bg-accent transition-opacity duration-200 ${
            mode === "view" ? "opacity-0" : "opacity-100"
          }`}
        />
      </div>
      <div ref={ringRef} className="absolute left-0 top-0 will-change-transform">
        <span
          className={`flex items-center justify-center rounded-full border transition-all duration-300 ${
            mode === "view"
              ? "-ml-10 -mt-10 h-20 w-20 border-accent bg-accent text-[10px] font-bold uppercase tracking-[0.2em] text-bone"
              : mode === "link"
              ? "-ml-6 -mt-6 h-12 w-12 border-accent/80 bg-accent/5"
              : "-ml-4 -mt-4 h-8 w-8 border-ink/30 bg-transparent"
          }`}
        >
          {mode === "view" ? "View" : ""}
        </span>
      </div>
    </div>
  );
}
